/**
 * @file components/dispatch/case-detail-role-guards.ts
 * @description 案件詳情頁的純前端角色守衛工具函式。
 *
 * 抽離自 `routes/_app/dispatch/$caseId.tsx`：原本將工具函式與 React 路由組件
 * 放在同一檔，會破壞 Vite Fast Refresh 的純組件 export 規則
 *（`hmr invalidate ... isDispatchManager export is incompatible`），
 * 因此獨立成模組。
 *
 * 後端 API 仍透過 `requireCapability(...)` 強制鑑權；本檔僅作為前端
 * defense-in-depth，控制 UI 顯隱與操作可用性。
 */

// 派遣類管理者角色集合（capability case.dispatch）
const DISPATCH_MANAGER_ROLES = new Set(['admin', 'manager']);

/**
 * 判斷用戶是否具有案件派遣管理權限（多角色：任一角色落在管理者集合即可）。
 *
 * @param roles - 用戶角色清單，可為 undefined 或 null（未登入時）
 * @returns true 表示具有派遣相關管理權限
 */
export function isDispatchManager(roles: readonly string[] | null | undefined): boolean {
  return !!roles && roles.some((r) => DISPATCH_MANAGER_ROLES.has(r));
}

interface FinalizeQuotationCtx {
  /** 是否具備 case.quotation.manage；由動態 RBAC 的 userCan 計算。 */
  canManageQuotations: boolean;
  workflowState: string;
  quotationCount: number;
  /**
   * 案件是否走預派案（cases.pre_dispatched_at 非 null，2026-09-11）。
   * 預派案的報價屬「派案後補辦」，案件早已離開「合約已簽」，
   * 故放寬狀態限制；後端 finalizeQuotation 對這類案件只留痕、不回退 workflowState。
   */
  preDispatched?: boolean;
}

/**
 * 是否允許在前端顯示「完成報價」按鈕：
 * 具報價管理能力 + 已建至少 1 份 quotation +
 * （workflowState='合約已簽' 或 案件為預派案補辦）。
 */
export function canFinalizeQuotation(ctx: FinalizeQuotationCtx): boolean {
  if (!ctx.canManageQuotations || ctx.quotationCount < 1) return false;
  return ctx.workflowState === '合約已簽' || !!ctx.preDispatched;
}

interface ConfirmDispatchCtx {
  roles: readonly string[] | null | undefined;
  workflowState: string;
  leadId: number | null;
}

/**
 * 是否允許在前端顯示「確認派遣」按鈕：派遣管理者 + workflowState='已收費' + 組長已指派。
 */
export function canConfirmDispatch(ctx: ConfirmDispatchCtx): boolean {
  return isDispatchManager(ctx.roles) && ctx.workflowState === '已收費' && ctx.leadId !== null;
}
